import * as Sentry from '@sentry/react';
import classnames from 'classnames';
import Fuse from 'fuse.js';
import { List, OrderedMap, Set } from 'immutable';
import { debounce, sortBy } from 'lodash';
import type React from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ArrayTwoOrMore, CustomUserData, QueryFilters, UserFilters, UserResponse } from 'stream-chat';

import type Account from '@/data/account/model';
import type User from '@/data/user/model';
import Button from '@/shared/ui/Button';
import FontIcon from '@/shared/ui/FontIcon';
import { USER_PAGE_SIZE, WORKCHAT_ERRORS } from '@/workchat/constants';
import UserPickerFilters from '@/workchat/v2/components/UserPickerFilters';
import { useStreamProvider } from '@/workchat/v2/providers/StreamProvider';
import { UserInfiniteList } from '@/workchat/v3/components/UserInfiniteList/UserInfiniteList';
import WorkChatAvatar from '@/workchat/v3/components/WorkChatAvatar/WorkChatAvatar';
import WorkChatListItem from '@/workchat/v3/components/WorkChatListItem/WorkChatListItem';

import '@/workchat/v3/components/UserPickerV3/UserPickerV3.scss';

const COLLAPSE_LIMIT = 5;
const QUERY_DEBOUNCE_TIME = 250;

interface UserPickerProps {
  user: User;
  account: Account;
  // Exclude these Users from the list
  excludeUserIds?: Set<string>;
  // Prepopulate Search input with these Users
  prefillUserIds?: List<string>;
  enableFilters?: boolean;
  onUserAdded?: (users: List<string>) => void;
  updateTrackedFilters?: (locations: Set<number>, positions: Set<number>) => void;
  collapsible?: boolean;
  // Limit configs
  limit?: number;
  limitText?: string;
  limitCTAButtonText?: string;
  onLimitCTAButtonClick?: () => void;
  userCache?: OrderedMap<string, UserResponse>;
  onUserCacheChange?: (userCache: OrderedMap<string, UserResponse>) => void;
}

/**
 * A User Picker component which queries Stream Users.
 * Optimized to search via Fuse (vs. Stream Query) when all the users have been loaded.
 */
export function UserPickerV3({
  user,
  account,
  excludeUserIds,
  prefillUserIds,
  enableFilters = false,
  onUserAdded,
  updateTrackedFilters,
  collapsible,
  limit,
  limitText,
  limitCTAButtonText,
  onLimitCTAButtonClick,
  userCache,
  onUserCacheChange,
}: UserPickerProps) {
  const { client } = useStreamProvider();

  const [term, setTerm] = useState('');
  const [added, setAdded] = useState(prefillUserIds || List<string>());
  const [expanded, setExpanded] = useState(true);
  const [selected, setSelected] = useState(-1);
  const [showFilters, setShowFilters] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const [filterLocations, setFilterLocations] = useState(Set<number>());
  const [filterPositions, setFilterPositions] = useState(Set<number>());

  // Caching state for initial users
  const [streamUserCache, setStreamUserCache] = useState<OrderedMap<string, UserResponse>>(userCache || OrderedMap());

  const [userIds, setUserIds] = useState<string[]>([]);
  const [hasMoreUsers, setHasMoreUsers] = useState(false);

  const [filteredUserIds, setFilteredUserIds] = useState<string[]>([]);
  const [hasMoreFilteredUsers, setHasMoreFilteredUsers] = useState(true);
  const [currentFilterQuery, setCurrentFilterQuery] = useState<UserFilters | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const streamFuse = useRef<Fuse<UserResponse>>(new Fuse([]));
  const inputRef = useRef<HTMLInputElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const pickedRef = useRef<HTMLDivElement>(null);

  const isAtLimit = limit && added.size >= limit;
  const targetUserIds = currentFilterQuery ? filteredUserIds : userIds;

  const baseQuery: UserFilters = useMemo(() => {
    return {
      teams: { $in: [`${account.id}`] },
    };
  }, [account.id]);

  const debounceUserQuery = useCallback(
    debounce(async ({ filter, sort, options, streamUserCache }: any) => {
      if (!filter) {
        setIsSearching(false);
        return;
      }

      const resp = await client?.queryUsers(filter, sort, options);

      let newStreamUserCache = streamUserCache;
      const userIds: string[] = [];
      const respUsers = sortBy(resp?.users ?? [], 'name');

      respUsers.forEach(streamUser => {
        newStreamUserCache = newStreamUserCache.set(streamUser.id, streamUser);
        userIds.push(streamUser.id);
      });

      setHasMoreFilteredUsers(respUsers.length === USER_PAGE_SIZE);
      setStreamUserCache(newStreamUserCache);
      onUserCacheChange?.(newStreamUserCache);
      setFilteredUserIds(userIds);
      setIsSearching(false);
    }, QUERY_DEBOUNCE_TIME),
    [client],
  );

  /**
   * Effect: Initial Load
   */
  useEffect(() => {
    loadMoreUsers(0);
  }, []);

  useEffect(() => {
    if (updateTrackedFilters) {
      updateTrackedFilters(filterLocations, filterPositions);
    }
  }, [filterLocations, filterPositions]);

  /**
   * Effect: Synchronize scroll event listeners.
   */
  useEffect(() => {
    const handleScrollEnd = () => {
      setIsScrolling(_isScrolling => false);
    };
    const handleScroll = () => {
      setIsScrolling(isScrolling => {
        if (isScrolling) {
          return isScrolling;
        }
        return true;
      });
    };

    if (pickedRef.current) {
      pickedRef.current.addEventListener('scrollend', handleScrollEnd);
      pickedRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (pickedRef.current) {
        pickedRef.current.removeEventListener('scrollend', handleScrollEnd);
        pickedRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [pickedRef.current]);

  const updateStreamFuse = (streamUserCache: OrderedMap<string, UserResponse>) => {
    const users = streamUserCache.valueSeq().toArray();

    streamFuse.current = new Fuse(users, {
      keys: ['name'],
      shouldSort: true,
      threshold: 0.25,
    });
  };

  const createFilterQuery = (
    searchTerm: string,
    positions: Set<number>,
    locations: Set<number>,
  ): UserFilters | null => {
    let resultQuery = { ...baseQuery };

    const searchTermExistsAndIsValid = !!searchTerm && searchTerm.trim() !== '';
    if (!searchTermExistsAndIsValid && positions.size === 0 && locations.size === 0) {
      return null;
    }

    if (searchTermExistsAndIsValid) {
      resultQuery.name = { $autocomplete: searchTerm };
    }

    // Build $or conditions for location and position filters
    const orConditions: UserFilters[] = [];

    // Add location filters if any are selected (locations are stored as strings in Stream)
    if (locations.size > 0) {
      const locationConditions = locations.toArray().map(locationId => ({
        locations: { $contains: `${locationId}` },
      }));
      // @ts-ignore ; Stream doesn't expose $contains and should fix their typings
      orConditions.push(...locationConditions);
    }

    // Add position filters if any are selected (positions are stored as strings in Stream)
    if (positions.size > 0) {
      const positionConditions = positions.toArray().map(positionId => ({
        positions: { $contains: `${positionId}` },
      }));
      // @ts-ignore ; Stream doesn't expose $contains and should fix their typings
      orConditions.push(...positionConditions);
    }

    // Handle filter conditions based on count
    if (orConditions.length > 1) {
      // Use $or when we have multiple conditions - cast to satisfy TypeScript's ArrayTwoOrMore requirement
      resultQuery.$or = orConditions as ArrayTwoOrMore<QueryFilters<CustomUserData>>;
    } else if (orConditions.length === 1) {
      // Merge single condition directly into baseQuery
      resultQuery = { ...resultQuery, ...orConditions[0] };
    }

    return resultQuery;
  };

  const searchUsersLocally = (searchTerm: string): string[] => {
    if (!searchTerm.trim().length) {
      return [];
    }

    const results = streamFuse.current.search(searchTerm);

    return results.map(({ item }) => item.id).filter(userId => !added.includes(userId));
  };

  const onToggle = (event: React.MouseEvent) => {
    event.stopPropagation();

    setExpanded(!expanded);
    setIsScrolling(false);

    if (!expanded) {
      setTimeout(() => inputRef.current?.focus());
    }
  };

  const onKey = (event: React.KeyboardEvent) => {
    const key = event.key;

    if (key === 'ArrowLeft') {
      if (document.activeElement === inputRef.current) {
        if (inputRef.current?.selectionStart !== 0 && inputRef.current?.selectionEnd !== 0) {
          return;
        }
        if (added.size) {
          divRef.current?.focus();
          setSelected(added.size - 1);
          return false;
        }
      } else if (selected > 0) {
        setSelected(selected - 1);
        return false;
      }
    } else if (key === 'ArrowRight') {
      if (document.activeElement === inputRef.current) {
        return;
      }
      if (selected < added.size - 1) {
        setSelected(selected + 1);
        return false;
      }
      setSelected(-1);
      inputRef.current?.select();
      return false;
    } else if (key === 'Backspace' || key === 'Delete') {
      if (document.activeElement !== inputRef.current && selected >= 0 && selected < added.size) {
        const newAdded = added.splice(selected, 1).toList();
        if (newAdded.size === 0) {
          setAdded(newAdded);
          onUserAdded?.(newAdded);
          setSelected(-1);
          inputRef.current?.select();
        } else if (selected >= newAdded.size) {
          setAdded(newAdded);
          onUserAdded?.(newAdded);
          setSelected(newAdded.size - 1);
        } else {
          setAdded(newAdded);
          onUserAdded?.(newAdded);
        }
      } else if (document.activeElement === inputRef.current && added.size > 0 && !term) {
        divRef.current?.focus();
        setSelected(added.size - 1);
      }
      return false;
    } else if (key === 'Tab' || key === 'Enter') {
      if (isAtLimit || !expanded) {
        return false;
      }

      if (userRowIds.length === 1) {
        addUser(userRowIds[0]);
      }
      return false;
    }
  };

  const onClickPicked = (_e: React.MouseEvent) => {
    setExpanded(true);
    setSelected(-1);
    inputRef.current?.focus();
  };

  const onClickName = (idx: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelected(idx);
    setExpanded(true);
    setTimeout(() => divRef.current?.focus(), 10);
  };

  const onClickInput = () => {
    setExpanded(true);
  };

  const onSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();

    const filterQuery = createFilterQuery(event.target.value, filterPositions, filterLocations);

    if (!filterQuery) {
      setIsSearching(false);
      setCurrentFilterQuery(filterQuery);
      setTerm('');

      return;
    }

    // If we have all the Users, switch to local search
    if (!hasMoreUsers && userIds.length && !filterPositions.size && !filterLocations.size) {
      setFilteredUserIds(searchUsersLocally(event.target.value));
      setTerm(event.target.value);
      setCurrentFilterQuery(filterQuery);
      return;
    }

    debounceUserQuery({
      streamUserCache,
      filter: filterQuery,
      sort: { name: 1 },
      options: { offset: 0, limit: USER_PAGE_SIZE },
    });

    setIsSearching(true);
    setCurrentFilterQuery(filterQuery);
    setTerm(event.target.value);
  };

  const toggleFilters = (event: React.MouseEvent) => {
    event.preventDefault();
    setShowFilters(!showFilters);
  };

  const addUser = (userId: string) => {
    const newAdded = added.push(userId);
    setAdded(newAdded);
    onUserAdded?.(newAdded);
    setSelected(-1);

    if (term) {
      setTerm('');

      const filterQuery = createFilterQuery('', filterPositions, filterLocations);
      setCurrentFilterQuery(filterQuery);

      if (filterLocations.size || filterPositions.size) {
        debounceUserQuery({
          streamUserCache,
          filter: filterQuery,
          sort: { name: 1 },
          options: { offset: 0, limit: USER_PAGE_SIZE },
        });

        setIsSearching(true);
      }
    }

    setTimeout(() => {
      inputRef.current?.focus();
    });
  };

  const renderFilters = () => {
    if (!showFilters) {
      return null;
    }

    return (
      <UserPickerFilters
        selectedPositions={filterPositions}
        selectedLocations={filterLocations}
        onClickOutside={() => setShowFilters(false)}
        onClear={() => {
          const positions = Set<number>();
          const locations = Set<number>();
          setFilterPositions(positions);
          setFilterLocations(locations);

          const filterQuery = createFilterQuery(term, positions, locations);
          setCurrentFilterQuery(filterQuery);
        }}
        onPositionFiltersChange={positions => {
          const filterQuery = createFilterQuery(term, positions, filterLocations);

          setFilterPositions(positions);
          setIsSearching(true);
          setCurrentFilterQuery(filterQuery);

          debounceUserQuery({
            streamUserCache,
            filter: filterQuery,
            sort: { name: 1 },
            options: { offset: 0, limit: USER_PAGE_SIZE },
          });
        }}
        onLocationFiltersChange={locations => {
          const filterQuery = createFilterQuery(term, filterPositions, locations);

          setFilterLocations(locations);
          setIsSearching(true);
          setCurrentFilterQuery(filterQuery);

          debounceUserQuery({
            streamUserCache,
            filter: filterQuery,
            sort: { name: 1 },
            options: { offset: 0, limit: USER_PAGE_SIZE },
          });
        }}
      />
    );
  };

  const shown = expanded ? added : added.slice(0, 2);
  const truncated = shown.size !== added.size;
  const numFilters = filterLocations.size + filterPositions.size;

  const userRowIds = targetUserIds.filter(displayUserId => {
    const isOtherThanMyself = displayUserId !== `${user.id}`;
    const existsInCache = streamUserCache.get(displayUserId);
    const notAdded = !added.contains(displayUserId);
    const notExcluded = !excludeUserIds || !excludeUserIds.contains(displayUserId);

    return isOtherThanMyself && existsInCache && notAdded && notExcluded;
  });

  const userRows: JSX.Element[] = userRowIds.map(userId => {
    const streamUser = streamUserCache.get(userId);

    return (
      <WorkChatListItem
        asButton
        key={`stream-user-${userId}`}
        className="user-row"
        hideChevron
        ariaLabel={`Select ${streamUser!.name}`}
        testId={`userpicker-stream-user-${userId}`}
        onClick={event => {
          addUser(userId);
          event.preventDefault();
        }}
        slotPrimary={
          <WorkChatAvatar
            avatarKey={`avatar-stream-user-${userId}`}
            avatarUrl={streamUser!.image}
            firstName={streamUser!.name?.split(' ')[0] || userId}
            lastName={streamUser!.name?.split(' ')[1] || ''}
          />
        }
        slotContent={streamUser!.name || userId}
      />
    );
  });

  const loadMoreUsers = async (offset = 0) => {
    try {
      const resp = await client?.queryUsers(
        currentFilterQuery || baseQuery,
        { name: 1 },
        { limit: USER_PAGE_SIZE, offset },
      );

      let newStreamUserCache = streamUserCache;
      const userIds: string[] = [...targetUserIds];
      const respUsers = sortBy(resp?.users ?? [], 'name');

      respUsers.forEach(streamUser => {
        newStreamUserCache = newStreamUserCache.set(streamUser.id, streamUser);
        userIds.push(streamUser.id);
      });

      if (currentFilterQuery) {
        setHasMoreFilteredUsers(respUsers.length === USER_PAGE_SIZE);
        setFilteredUserIds(userIds);
      } else {
        setHasMoreUsers(respUsers.length === USER_PAGE_SIZE);
        setUserIds(userIds);
        updateStreamFuse(newStreamUserCache);
      }
      setStreamUserCache(newStreamUserCache);
      onUserCacheChange?.(newStreamUserCache);

      return userIds;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          workchatVersion: 3,
          workchatError: WORKCHAT_ERRORS.failedToQueryStreamUsers,
        },
      });
    }

    return [];
  };

  const renderUserList = () => {
    if (isAtLimit) {
      return (
        <div data-testid="userpicker-limit-reached" className="limit-reached">
          <p>{limitText}</p>
          <Button
            type="button"
            className="cta-button"
            color="secondary"
            size="sm"
            onClick={() => onLimitCTAButtonClick?.()}
          >
            {limitCTAButtonText}
          </Button>
        </div>
      );
    }

    if (isSearching) {
      return (
        <div data-testid="userpicker-searching" className="searching">
          Loading...
        </div>
      );
    }

    if (term && !targetUserIds.length && !filterLocations.size && !filterPositions.size) {
      return (
        <div data-testid="userpicker-no-results" className="no-results">
          Sorry, it appears this person is not a part of {account.company}
        </div>
      );
    }

    const hasMore = currentFilterQuery ? hasMoreFilteredUsers : hasMoreUsers;

    return (
      <UserInfiniteList
        key={term || 'initial'}
        userRows={userRows}
        hasMore={hasMore}
        loadMore={() => loadMoreUsers(targetUserIds.length)}
      />
    );
  };

  const handleRemoveUser = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: string) => {
    const newAdded = added.filter(id => id !== userId);
    setAdded(newAdded);
    onUserAdded?.(newAdded);
    setSelected(-1);

    if (!expanded) {
      setExpanded(true);
    }
    setTimeout(() => inputRef.current?.focus());

    event?.stopPropagation();
  };

  const renderCollapsibleControl = () => {
    if (!collapsible || added.size < COLLAPSE_LIMIT) {
      return null;
    }

    return (
      <div className={classnames('expand-button-container', { hidden: isScrolling && expanded })}>
        <button
          data-testid="userpicker-expand-button"
          className="expand-button"
          type="button"
          onClick={onToggle}
          aria-label="Toggle Channel"
        >
          {expanded ? (
            <FontIcon icon="chevron-up" srText="Show All Names" />
          ) : (
            <Fragment>
              <span className="expand-count">{[added.size - shown.size, ' more']}</span>
              <FontIcon key="collapseything" icon="chevron-down" srText="Show Less Names" />
            </Fragment>
          )}
        </button>
      </div>
    );
  };

  const renderNameChips = () => {
    return shown
      .toArray()
      .filter(userId => !!streamUserCache.get(`${userId}`))
      .map((userId, index) => {
        const streamUser = streamUserCache.get(`${userId}`);
        return (
          // biome-ignore lint/a11y/noStaticElementInteractions: required for chip-like components with embedded remove button
          <div
            className="name"
            key={`userpicker-added-user-${streamUser!.id}`}
            onClick={event => onClickName(index, event)}
          >
            <span className={classnames('name-container', { selected: selected === index })}>
              {streamUser!.name}{' '}
              <button type="button" className="remove-user" onClick={event => handleRemoveUser(event, userId)}>
                <FontIcon icon="close" />
              </button>
            </span>
          </div>
        );
      });
  };

  return (
    <div className="UserPickerV3">
      {/** biome-ignore lint/a11y/noStaticElementInteractions: required for keyboard navigation */}
      <div ref={divRef} className={classnames('picked-box', { truncated })} tabIndex={-1} onKeyDown={onKey}>
        <div className="search-container">
          {/** biome-ignore lint/a11y/noStaticElementInteractions: required for pseudo input container */}
          <div ref={pickedRef} className={classnames('picked', { collapsible })} onClick={onClickPicked}>
            {renderNameChips()}
            <input
              type="text"
              data-test-id="userpicker-search-input"
              ref={inputRef}
              className={classnames('search-input', { 'has-text': !!term, hidden: isAtLimit || !expanded })}
              placeholder={added.size > 0 ? '' : 'Search for members...'}
              value={term}
              onChange={onSearchInputChange}
              onClick={onClickInput}
            />
          </div>

          {renderCollapsibleControl()}
        </div>

        {enableFilters ? (
          <button data-testid="userpicker-filter-button" type="button" className="btn filter" onClick={toggleFilters}>
            <FontIcon icon="preferences" srText="Position and schedule filters" />
          </button>
        ) : null}
        {numFilters ? (
          <span data-testid="userpicker-filter-count" className="filter-count">
            {numFilters}
          </span>
        ) : null}
        {renderFilters()}
      </div>
      <div className="user-list">{renderUserList()}</div>
    </div>
  );
}
